import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {ReactEditor, useSlate, useSlateSelection} from "slate-react";

import AskKhanmigoMenu from "./ask-khanmigo-menu.tsx";
import {editorToMarkdownComponents} from "./slate-to-markdown.ts";
import {getParentBlockForSuggestions} from "./slate/helpers/block-helpers.ts";
import getSuggestionsForCurrentSelection, {
    type Suggestion,
} from "./suggestions.ts";

/**
 * A component that controls the popup that appears over your current selection
 * within the coeditor when we have "suggestions" for you (changes or requests
 * you may want to make about your selection).
 *
 * This component shouldn't deal with styling or presentation; see
 * AskKhanmigoDropdown for that. This component is meant to deal mostly with
 * hiding, showing, and positioning the popup.
 */
export default function AskKhanmigoPopover(props: {
    handleSuggestionClick: (suggestion: Suggestion) => void;
}) {
    const {handleSuggestionClick} = props;

    const editor = useSlate();
    const selection = useSlateSelection();

    const [manuallyClosed, setManuallyClosed] = useState(false);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [yOffset, setYOffset] = useState(0);

    const clickSuggestionAndHide = useCallback(
        (suggestion: Suggestion) => {
            handleSuggestionClick(suggestion);
            setManuallyClosed(true);
        },
        [handleSuggestionClick],
    );

    useEffect(() => {
        if (!selection) {
            return;
        }

        // When the selection changes, we want to do two things:
        // First, we recalculate the suggestions for the current cursor:
        const doc = editorToMarkdownComponents(editor);
        const suggestions = getSuggestionsForCurrentSelection(doc);
        setSuggestions(suggestions);
        setManuallyClosed(false);
        if (!suggestions.length) {
            return;
        }

        // We need to reposition the popover to be centered vertically
        // relative to the selected block node. That means we need to
        // find the nearest parent block containing the selection:
        const parentBlock = getParentBlockForSuggestions(editor);
        if (!parentBlock) {
            setSuggestions([]);
            return;
        }

        const parentDomNode = ReactEditor.toDOMNode(editor, parentBlock);
        const editorNode = ReactEditor.toDOMNode(editor, editor);

        const height = parentDomNode.offsetHeight;

        setYOffset(
            // Then take the editor's Y offset:
            editorNode.offsetTop +
                // Add the offset of the block node relative to the editor:
                parentDomNode.offsetTop +
                // And add half the height of the block node, so that we
                // get the offset of the vertical center of the block node:
                height / 2,
        );
    }, [selection, editor]);

    if (manuallyClosed || !suggestions.length) {
        return null;
    }

    return (
        <AskKhanmigoMenu
            yOffset={yOffset}
            suggestions={suggestions}
            handleSuggestionClick={clickSuggestionAndHide}
        />
    );
}
